<template>
  <div class="wrong-app-page">
    <div class="wrong-app-shell">
      <section class="brand-panel">
        <div class="brand-top">
          <img :src="currentLogo" alt="Scope E-inspectie" class="brand-logo" />
          <span class="brand-badge">Verkeerde omgeving</span>
        </div>
        <div class="brand-content">
          <h1 class="brand-title">U staat bijna goed</h1>
          <p class="brand-subtitle">
            Deze omgeving is bedoeld voor opdrachtgevers en installateurs.
          </p>
        </div>
        <ul class="brand-list">
          <li>
            <i class="pi pi-user"></i>
            <span>U bent ingelogd als <strong>{{ userTypesLabel }}</strong></span>
          </li>
          <li>
            <i class="pi pi-compass"></i>
            <span>Kies de juiste applicatie om verder te gaan</span>
          </li>
          <li>
            <i class="pi pi-shield"></i>
            <span>Uw sessie blijft veilig tijdens het wisselen</span>
          </li>
        </ul>
        <div class="brand-note">
          <i class="pi pi-info-circle"></i>
          <span>Heeft u vragen? Neem contact op met uw inspectiepartner.</span>
        </div>
      </section>

      <section class="action-panel">
        <div class="action-card">
          <div class="action-header">
            <div class="icon-container">
              <i class="pi pi-exclamation-triangle"></i>
            </div>
            <div>
              <h2>Verkeerde applicatie</h2>
              <p>Ga verder in de omgeving die bij uw rol past.</p>
            </div>
          </div>
          <div class="action-buttons">
            <Button :label="`Ga naar ${appLabel}`" icon="pi pi-external-link" @click="redirectToApp" class="w-full" />
            <Button label="Uitloggen" icon="pi pi-sign-out" severity="secondary" @click="handleLogout" class="w-full" />
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import { useRouter } from 'vue-router'
  import { useAuthStore } from '../stores/auth'
  import { useThemeStore } from '../stores/theme'
  import { getFrontendUrl, type UserType } from '../composables/useUserTypeCheck'
  import Button from 'primevue/button'
  import logoLight from '../assets/logoLight.svg'
  import logoDark from '../assets/logoDark.svg'

  const props = defineProps<{
    userTypes: UserType[]
    redirectUrl: string
  }>()

  const router = useRouter()
  const authStore = useAuthStore()
  const themeStore = useThemeStore()
  const currentLogo = computed(() => themeStore.isDark ? logoDark : logoLight)

  const typeLabels: Record<UserType, string> = {
    opdrachtgever: 'opdrachtgever',
    installateur: 'installateur',
    ibedrijf: 'inspectiebedrijf',
    inspecteur: 'inspecteur',
    unknown: 'gebruiker'
  }

  const userTypesLabel = computed(() => {
    return props.userTypes
      .map(t => typeLabels[t] || t)
      .join(' en ')
  })

  const appLabel = computed(() => {
    return 'Inspectie App'
  })

  function redirectToApp() {
    window.location.href = props.redirectUrl || getFrontendUrl()
  }

  async function handleLogout() {
    await authStore.logout()
    router.push('/login')
  }
</script>

<style scoped>
  .wrong-app-page {
    min-height: 100vh;
    padding: 2.5rem 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    max-width: none;
    margin: 0;
    background:
      radial-gradient(circle at 18% 18%, var(--primary-alpha-10), transparent 55%),
      radial-gradient(circle at 82% 82%, var(--color-warning-light), transparent 50%),
      linear-gradient(120deg, var(--p-background-0), var(--p-background-50));
    position: relative;
    overflow: hidden;
  }

  .wrong-app-page::after {
    content: '';
    position: absolute;
    top: -120px;
    right: -120px;
    width: 280px;
    height: 280px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.6), transparent 65%);
    opacity: 0.35;
    pointer-events: none;
  }

  .wrong-app-shell {
    width: 100%;
    max-width: 1100px;
    display: grid;
    grid-template-columns: minmax(320px, 1.05fr) minmax(320px, 0.95fr);
    gap: 2rem;
    position: relative;
    z-index: 1;
  }

  .brand-panel {
    background: linear-gradient(150deg, var(--p-background-0), var(--p-background-100));
    border-radius: var(--radius-md);
    border: 1px solid var(--p-background-200);
    padding: 2.5rem;
    box-shadow: var(--shadow-lg);
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    animation: rise-in 0.6s ease both;
  }

  .brand-top {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
  }

  .brand-logo {
    height: 36px;
    width: auto;
  }

  .brand-badge {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    font-weight: 600;
    color: var(--p-primary-700);
    background: var(--primary-alpha-10);
    padding: 0.35rem 0.75rem;
    border-radius: 999px;
  }

  .brand-title {
    font-size: 2.1rem;
    line-height: 1.15;
    margin: 0;
    color: var(--p-background-900);
  }

  .brand-subtitle {
    margin: 0.5rem 0 0;
    font-size: 1rem;
    line-height: 1.65;
    color: var(--p-background-600);
    max-width: 26rem;
  }

  .brand-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    gap: 0.75rem;
  }

  .brand-list li {
    display: grid;
    grid-template-columns: 1.75rem 1fr;
    align-items: center;
    gap: 0.75rem;
    color: var(--p-background-700);
    font-size: 0.95rem;
  }

  .brand-list i {
    color: var(--p-primary-500);
    font-size: 1.1rem;
  }

  .brand-list strong {
    color: var(--p-background-900);
  }

  .brand-note {
    margin-top: auto;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.85rem 1rem;
    border-radius: var(--radius-md);
    border: 1px solid var(--p-background-200);
    background: var(--p-background-0);
    color: var(--p-background-600);
    font-size: 0.9rem;
  }

  .brand-note i {
    color: var(--p-primary-500);
    font-size: 1.1rem;
  }

  .action-panel {
    display: flex;
    align-items: center;
  }

  .action-card {
    background: var(--p-background-0);
    border: 1px solid var(--p-background-200);
    border-radius: var(--radius-md);
    padding: 2.25rem;
    box-shadow: var(--shadow-lg);
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    animation: rise-in 0.6s ease both;
    animation-delay: 0.1s;
  }

  .action-header {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .action-header h2 {
    margin: 0;
    font-size: 1.5rem;
    color: var(--p-background-900);
  }

  .action-header p {
    margin: 0.35rem 0 0;
    color: var(--p-background-600);
    font-size: 0.95rem;
  }

  .icon-container {
    width: 3rem;
    height: 3rem;
    border-radius: var(--radius-md);
    background: var(--p-background-100);
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--p-background-400);
  }

  .icon-container i {
    font-size: 1.6rem;
    color: var(--p-background-600);
  }

  .action-buttons {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .w-full {
    width: 100%;
  }

  @keyframes rise-in {
    from {
      opacity: 0;
      transform: translateY(16px);
    }

    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (prefers-reduced-motion: reduce) {

    .brand-panel,
    .action-card {
      animation: none;
    }
  }

  @media (max-width: 960px) {
    .wrong-app-shell {
      grid-template-columns: 1fr;
    }

    .brand-panel {
      order: 2;
    }

    .action-panel {
      order: 1;
    }
  }

  @media (max-width: 640px) {
    .wrong-app-page {
      padding: 1.5rem 1rem;
    }

    .brand-panel,
    .action-card {
      padding: 1.75rem;
    }

    .brand-title {
      font-size: 1.7rem;
    }
  }
</style>
